import { mutatePurchaseOrders } from "@/api/purchaseOrder/usePurchaseOrder";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormButtonBox } from "@/features/standard/Form/FormButtonBox";
import { FormSelect } from "@/features/standard/Form/FormSelect";
import { FormWrapper } from "@/features/standard/Form/FormWrapper";
import { Main } from "@/features/standard/Main";
import customAlert from "@/features/ui/alert/alert";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { PartnerAutoComplete } from "@/features/ui/autoComplete/partner/partner-autoComplete";
import { PurchaseOrderCategoryAutoComplete } from "@/features/ui/autoComplete/purchaseOrderCategory/purchaseOrderCategory-autoComplete";
import { UserAutoComplete } from "@/features/ui/autoComplete/user/user-autoComplete";
import { useUserStore } from "@/store/userStore";
import { getUserCodeByUser } from "@/utils/checkData";
import { handleErrorResponse } from "@/utils/errorMessage";
import { translateWithPartnerType } from "@/utils/partnerType";
import { validateFormValues } from "@/utils/validation";
import { Button, Select, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import {
  PartnersGet200ResponseRowsInnerTypeEnum,
  PurchaseOrdersGet200ResponseRowsInner,
  ScmApiPurchaseOrdersPurchaseOrderIdPutRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconCalendar } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const Update = Object.assign({}, Main, {
  Button: Button,
  Input: TextInput,
  Select: FormSelect,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  DateInput: DateInput,
});

interface PurchaseOrderUpdateFormProps {
  formatterProps: PurchaseOrdersGet200ResponseRowsInner | undefined;
}

export const PurchaseOrderUpdateForm = (params: PurchaseOrderUpdateFormProps) => {
  const { formatterProps } = params;
  const userCode = getUserCodeByUser();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { userInfo } = useUserStore();
  const partnerType = userInfo?.partner?.type;

  const { mutate: updateMutate } = useMutation(
    (params: ScmApiPurchaseOrdersPurchaseOrderIdPutRequest) =>
      mutatePurchaseOrders
        .update(params)
        .mutationFn(params as ScmApiPurchaseOrdersPurchaseOrderIdPutRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["purchaseOrders"]);
        closeModal(formatterProps);
        customAlert(
          t("message.editSuccess", {
            item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
          }),
          t("common.editSuccess", {
            item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
          }),
          "green"
        );
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({
            error,
            errorContext: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
          }),
          t("common.editFail", {
            item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
          }),
          "red"
        );
      },
    }
  );

  const { closeModal } = useModal();

  const form = useForm({
    initialValues: {
      partnerCode: formatterProps?.partnerCode,
      status: formatterProps?.status,
      code: formatterProps?.code,
      name: formatterProps?.name,
      purchaseOrderCategoryName: formatterProps?.purchaseOrderCategoryName,
      userCode: formatterProps?.userCode,
      sourceLocationCode: formatterProps?.sourceLocationCode,
      orderDate: formatterProps?.orderDate ? new Date(formatterProps.orderDate) : null,
      deliveryDeadline: formatterProps?.deliveryDeadline
        ? new Date(formatterProps.deliveryDeadline)
        : null,
    },
  });

  const onSubmit = async () => {
    try {
      updateMutate({
        purchaseOrderId: formatterProps?.id ?? 0,
        purchaseOrdersGetRequest: {
          direction: "INBOUND",
          partnerCode: form.values.partnerCode!,
          code: form.values.code!,
          name: form.values.name!,
          purchaseOrderCategoryName: form.values.purchaseOrderCategoryName!,
          status: form.values.status!,
          userCode: form.values.userCode!,
          sourceLocationCode: form.values.sourceLocationCode!,
          orderDate: form.values.orderDate?.toDateString()!,
          deliveryDeadline: form.values.deliveryDeadline?.toDateString()!,
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({
          error,
          errorContext: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
        }),
        t("common.editFail", {
          item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
        }),
        "red"
      );
    }
  };

  return (
    <>
      <Update.Wrapper>
        <PartnerAutoComplete
          withAsterisk
          label={t("partner.partner")}
          placeholder={t("placeholder.select", { item: t("partner.partner") })}
          defaultValue={formatterProps?.partnerCode}
          {...form.getInputProps("partnerCode")}
          partnerType={[
            partnerType ?? PartnersGet200ResponseRowsInnerTypeEnum.SALES,
            PartnersGet200ResponseRowsInnerTypeEnum.BI_DIRECTION,
          ]}
          error={form.isDirty("partnerCode") ? t("common.toSave") : ""}
        />
        <Update.Input
          withAsterisk
          label={translateWithPartnerType(t, "purchaseOrder.code", partnerType)}
          placeholder={t("placeholder.input", {
            item: translateWithPartnerType(t, "purchaseOrder.code", partnerType),
          })}
          defaultValue={formatterProps?.code}
          {...form.getInputProps("code")}
          error={form.isDirty("code") ? t("common.toSave") : ""}
          styles={{
            input: {
              color: form.isDirty("code") ? "red !important" : "",
              borderColor: form.isDirty("code") ? "red !important" : "",
            },
            error: { fontSize: 11, color: "red" },
          }}
        />
        <Update.Input
          withAsterisk
          label={translateWithPartnerType(t, "purchaseOrder.name", partnerType)}
          placeholder={t("placeholder.input", {
            item: translateWithPartnerType(t, "purchaseOrder.name", partnerType),
          })}
          defaultValue={formatterProps?.name}
          {...form.getInputProps("name")}
          error={form.isDirty("name") ? t("common.toSave") : ""}
          styles={{
            input: {
              color: form.isDirty("name") ? "red !important" : "",
              borderColor: form.isDirty("name") ? "red !important" : "",
            },
            error: { fontSize: 11, color: "red" },
          }}
        />
        <PurchaseOrderCategoryAutoComplete
          label={translateWithPartnerType(t, "purchaseOrder.categoryName", partnerType)}
          placeholder={t("placeholder.select", {
            item: translateWithPartnerType(t, "purchaseOrder.categoryName", partnerType),
          })}
          {...form.getInputProps("purchaseOrderCategoryName")}
          error={form.isDirty("purchaseOrderCategoryName") ? t("common.toSave") : ""}
        />
        <Select
          label={translateWithPartnerType(t, "purchaseOrder.status", partnerType)}
          placeholder={t("placeholder.select", {
            item: translateWithPartnerType(t, "purchaseOrder.status", partnerType),
          })}
          data={[
            { value: "DRAFT", label: t("purchaseStatus.DRAFT") },
            { value: "IN_PROGRESS", label: t("purchaseStatus.IN_PROGRESS") },
            { value: "DONE", label: t("purchaseStatus.DONE") },
            { value: "CANCELED", label: t("purchaseStatus.CANCELED") },
          ]}
          defaultValue={formatterProps?.status}
          {...form.getInputProps("status")}
          error={form.isDirty("status") ? t("common.toSave") : ""}
          styles={{
            input: {
              color: form.isDirty("status") ? "red !important" : "",
              borderColor: form.isDirty("status") ? "red !important" : "",
            },
            error: { fontSize: 11, color: "red" },
          }}
        />
        <UserAutoComplete
          withAsterisk
          label={t("order.manager")}
          placeholder={t("placeholder.select", { item: t("order.manager") })}
          defaultValue={formatterProps?.userCode}
          {...form.getInputProps("userCode")}
          error={form.isDirty("userCode") ? t("common.toSave") : ""}
        />
        <LocationAutoComplete
          label={translateWithPartnerType(t, "purchaseOrder.toLocationName", partnerType)}
          placeholder={t("placeholder.select", {
            item: translateWithPartnerType(t, "purchaseOrder.toLocationName", partnerType),
          })}
          defaultValue={formatterProps?.sourceLocationCode}
          {...form.getInputProps("sourceLocationCode")}
          error={form.isDirty("sourceLocationCode") ? t("common.toSave") : ""}
        />
        <Update.DateInput
          popoverProps={{ withinPortal: true }}
          icon={<IconCalendar size="1.2rem" />}
          label={t("purchaseOrder.orderDate")}
          valueFormat="YYYY-MM-DD"
          locale="ko"
          firstDayOfWeek={0}
          {...form.getInputProps("orderDate")}
          error={form.isDirty("orderDate") ? t("common.toSave") : ""}
          styles={{
            input: {
              color: form.isDirty("orderDate") ? "red !important" : "",
              borderColor: form.isDirty("orderDate") ? "red !important" : "",
            },
            error: { fontSize: 11, color: "red" },
          }}
        />
        <Update.DateInput
          popoverProps={{ withinPortal: true }}
          icon={<IconCalendar size="1.2rem" />}
          label={t("order.deliveryDate")}
          valueFormat="YYYY-MM-DD"
          locale="ko"
          firstDayOfWeek={0}
          minDate={form.values.orderDate ? new Date(form.values.orderDate) : undefined}
          {...form.getInputProps("deliveryDeadline")}
          error={form.isDirty("deliveryDeadline") ? t("common.toSave") : ""}
          styles={{
            input: {
              color: form.isDirty("deliveryDeadline") ? "red !important" : "",
              borderColor: form.isDirty("deliveryDeadline") ? "red !important" : "",
            },
            error: { fontSize: 11, color: "red" },
          }}
        />
        <Update.ButtonBox>
          <Update.Button color="gray" onClick={closeModal}>
            {t("common.cancel")}
          </Update.Button>
          <Update.Button
            disabled={validateFormValues({
              pageType: "purchaseOrder",
              values: form.values,
            })}
            onClick={onSubmit}
          >
            {t("common.edit", {
              item: translateWithPartnerType(t, "purchaseOrder.purchaseOrder", partnerType),
            })}
          </Update.Button>
        </Update.ButtonBox>
      </Update.Wrapper>
    </>
  );
};
